import { API_URL } from "@/config";
import { axios } from '@/lib/axios';
import { generateLSKey, getLSitem } from '@/utils/cacheLS';
import { ListContextById, SectionsType, ListContext } from '@/types';
import { FeatureFlag } from "@/features/FeatureFlags/types";
import type { CdnSigningParams } from '@/utils/cdnSigning';

type GetInstitutionUserSubscriptionBodyStructure = {
    navigation: {
        page: number;
        itemsOnPage: number;
    };
    totalCount: boolean;
    filter: {
        collection: {
            bool_operator: string;
            condition: {
                property: string;
                operator: string;
                value: string | number | boolean;
            };
        }[];
    };
    sort: string[][];
};


export const fetchSearch = (query: string | null, contentType: string, page: number, from: string | null, to: string | null): Promise<any> => {
    return axios.get(`${API_URL}/api/free/search/?query=${query}&content_type=${contentType}&page=${page}&from=${from}&to=${to}`);
};

export const editEditorCollectionRank = (collectionId: number, id: number, data: { rank: number }) => {
    return axios.patch(`${API_URL}/api/admin/collections/${collectionId}/editor/${id}`, data);
};

export const fetchArticleByDomainId = (language: string, domainId: number | string): Promise<any> => {
    return axios.get(`${API_URL}/api/free/article/${language}/${domainId}`);
};

export type CdnSigningRefreshResponse = {
    articleId: number;
    cdnFile: string;
    cdnSigning: CdnSigningParams;
};

/** Refresh CloudFront HLS signing credentials for an article (long playback sessions). */
export const fetchCdnSigningRefresh = (
    articleId: number,
    params?: { locale?: string }
): Promise<CdnSigningRefreshResponse> => {
    const query = new URLSearchParams();
    if (params?.locale) {
        query.set('locale', params.locale);
    }
    const qs = query.toString();
    const url = `${API_URL}/api/free/cdn-signing/article/${articleId}`;
    return axios.get(qs ? `${url}?${qs}` : url);
};

export const fetchSectionListById = (language: string, id: number | string, page?: number, itemsPerPage?: number): Promise<ListContextById> => {
    const searchStr = `${page || itemsPerPage ? '?' : ''}${page ? `page=${page}&` : ''}${itemsPerPage ? `items_per_page=${itemsPerPage}` : ''}`;
    const lsKey = generateLSKey(language, id, 'list-' + searchStr);
    return getLSitem(lsKey) || axios.get(`${API_URL}/api/free/section/list/${language}/${id}${searchStr}`);
};

export const getSectionForSubscription = (): Promise<SectionsType> => {
    return axios.get(`${API_URL}/api/section_subscription`);
};

export const fetchSectionList = (language: string): Promise<ListContext> => {
    const lsKey = generateLSKey(language, 0, 'list');
    return getLSitem(lsKey) || axios.get(`${API_URL}/api/free/section/list/${language}`);
};

export const fetchAdminSectionListById = (id: number | string, page?: number, itemsPerPage?: number): Promise<ListContextById> => {
    const searchStr = `${page || itemsPerPage ? '?' : ''}${page ? `page=${page}&` : ''}${itemsPerPage ? `items_per_page=${itemsPerPage}` : ''}`;
    return axios.get(`/api/admin/section/list/${id}${searchStr}`);
};

export const getInstitutionSubscription = (instId: number, data: GetInstitutionUserSubscriptionBodyStructure) => {
    return axios.post(`${API_URL}/api/admin/subscription/institution/institute`, data) as Promise<{ result: any[] }>;
};

export const getInstitutionUserSubscription = (data: GetInstitutionUserSubscriptionBodyStructure) => {
    return axios.post(`${API_URL}/api/admin/subscription/institution/user`, data) as Promise<{ result: any[] }>;
};

type EndDateMode = 'end-of-term' | 'immediately' | 'custom';
export interface CancelSubscriptionPayload {
    endDateMode: EndDateMode;
    customDate?: string;
}

export const cancelSubscription = (subscriptionId: number, payload: CancelSubscriptionPayload) =>
    axios.patch(`${API_URL}/api/admin/subscription/${subscriptionId}/cancel`, payload).then((r) => r.data);

export const getSubscriptionById = (id: number) =>
    axios.get(`${API_URL}/api/admin/subscription/${id}`);

export const getSiteSettings = <T = any>(name: string): Promise<FeatureFlag<T>> => {
    return axios.get(`${API_URL}/api/free/site_settings/${name}`);
};

